import {FormElement} from "./form-element.js";
import {OptionTools} from "./option-tools.js";
import {CheckElement} from "./check-element.js";

/**
 * Represents a multi-select form element that allows selection of multiple options.
 * This class extends the FormElement base class and provides functionality for rendering
 * and managing a group of checkable options.
 */
export class MultiSelectElement extends FormElement {
    options;
    wrapper;
    noValue = false;
    
    checkboxElements = [];
    
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);
        this.checkboxElements = [];
    }

    /**
     * Sets the label for the specified wrapper by prepending a generated component label.
     *
     * @param {HTMLElement} wrapper - The wrapper element to which the label is added.
     * @param {Object} component - The component associated with the label.
     * @return {void} This method does not return a value.
     */
    setLabel(wrapper, component) {
        wrapper.prepend(this.componentLabel());
    }

    /**
     * Adds a class to the provided wrapper element and renders a collection of check elements based on the given options.
     *
     * @param {HTMLElement} wrapper - The HTML element that will serve as the container for the rendered components.
     * @return {null} Always returns null after rendering the components.
     */
    component(wrapper) {
        wrapper.classList.add('multi-select-group');
        this.wrapper = wrapper;
        
        this.setOptions(wrapper);
        this.defaultListeners(this.domElement);
        
        return null;
    }

    defaultListeners(component) {
        super.defaultListeners(component);
    }
    
    setOptions(wrapper) {
        OptionTools.clearOptions(this.checkboxElements);
        let rootFormData = this.rootForm.formData;
        let formDataToUse = {}; 
        if (!OptionTools.isDynamicOptions(this.options)) formDataToUse = this.formData;
        else {
            let dependsOn = this.options.dependsOn; 
            if (typeof dependsOn === "string") {
                formDataToUse = OptionTools.findKeyInRootFormData(rootFormData, dependsOn) ?? {};
            } else if (Array.isArray(dependsOn)) {
                if (dependsOn.length === 1) formDataToUse = OptionTools.findKeyInRootFormData(rootFormData, dependsOn[0]) ?? {};
                else if (dependsOn.length === 2) {
                    if (OptionTools.doKeysShareFormData(rootFormData, dependsOn[0], dependsOn[1])) {
                        formDataToUse = OptionTools.findKeyInRootFormData(rootFormData, dependsOn[0]);
                    } else {
                        let alephFormData = OptionTools.findKeyInRootFormData(rootFormData, dependsOn[0]);
                        let betFormData = OptionTools.findKeyInRootFormData(rootFormData, dependsOn[1]);
                        formDataToUse = {...alephFormData, ...betFormData};
                    }
                }

            }
        }
        const options = OptionTools.buildOptions(this, this.options, formDataToUse);
        options.forEach(option => {
            const check = Object.assign(new CheckElement(this.formData, wrapper, this.collection), option);
            check.parent = this;
            check.render();
            
            this.checkboxElements.push(check);
        });
    }

    changeEvent(data) {
        this.setOptions(this.wrapper);
    }
}
